import { useMemo } from 'react'
import usePageInfo from '../../hooks/usePageInfo'
import { useSearchParams } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'

const useCompareMode = () => {
    const [urlParams] = useSearchParams()
    const isMobile = useMediaQuery('(max-width:600px)')
    const { label: currentGraphName } = usePageInfo()

    const originalGraphNames = urlParams.getAll('addcontext')

    const mode = {
        merge: urlParams.get('merge'),
        difference: urlParams.get('highlight') || urlParams.get('missing'),
        niche:
            (urlParams.get('highlight') || urlParams.get('missing')) &&
            userFactory.getCurrentApp() == 'niche',
        seo:
            (urlParams.get('highlight') || urlParams.get('missing')) &&
            userFactory.getCurrentApp() == 'seo',
        interpret: urlParams.get('interpret'),
    }

    const compareModeTitle = useMemo(() => {
        if (mode.merge) return 'Combination'
        if (mode.difference) return 'Difference'
        if (mode.interpret) return 'Embedding'
        if (originalGraphNames.length > 0) return 'Intersection'
        return null
    }, [mode, originalGraphNames])

    // compare-mode flag — URL-derived
    const isCompareMode = Boolean(compareModeTitle)

    const getGraphWording = shouldIncludeGraphNames => {
        if (mode.merge)
            return `${originalGraphNames.join(
                ' and '
            )} merged with ${currentGraphName}`

        if (mode.niche) {
            return shouldIncludeGraphNames
                ? `People search for (${originalGraphNames.join(
                      ' and '
                  )}), but don't find (${currentGraphName})`
                : `People search for, but don't find`
        } else if (mode.seo) {
            return shouldIncludeGraphNames
                ? `In search queries (${originalGraphNames.join(
                      ' and '
                  )}), but not in results (${currentGraphName})`
                : `In search queries, but not in results`
        } else if (mode.difference) {
            return `In ${originalGraphNames.join(
                ' and '
            )}, but not in ${currentGraphName}`
        }

        if (mode.interpret)
            return `${currentGraphName} highlighted in ${originalGraphNames.join(
                ' and '
            )}`
        return `${originalGraphNames.join(
            ' and '
        )} overlap with ${currentGraphName}`
    }

    const compareModeDescription = useMemo(() => {
        if (mode.merge) return `Union — ${getGraphWording(true)}`
        if (mode.difference) return `Difference — ${getGraphWording(true)}`
        if (mode.interpret) return `Embedding — ${getGraphWording(true)}`
        return `Intersection — ${getGraphWording(true)}`
    }, [mode])

    const originalGraphLabel = useMemo(() => {
        return `Original Graph: ${originalGraphNames}`
    }, [originalGraphNames])

    const currentGraphLabel = useMemo(() => {
        return `Current Graph: ${currentGraphName}`
    }, [currentGraphName])
    const getCompareChipLabel = selectedMode => {
        if (selectedMode === 'Current') return currentGraphLabel
        if (selectedMode === 'Original') return originalGraphLabel
        if (isMobile) return `Perspective: ${compareModeTitle}`
        return `Perspective: ${getGraphWording()}`
    }

    return {
        getGraphWording,
        compareMode: mode,
        isCompareMode,
        originalGraphLabel,
        currentGraphLabel,
        compareModeTitle,
        compareModeDescription,
        getCompareChipLabel,
    }
}

export default useCompareMode
